/**
 * Driver "Ready to Depart" readiness rule (DTP-110609).
 *
 * Single home for the departure checklist, shared by ReadinessChecklist and
 * DriverAssignmentDetail so the two can't drift. Verification is scoped to
 * the leg (`trailer_events.leg_id`) so a trailer verified on an earlier job
 * never satisfies a new leg.
 */

export interface DriverVerifiedEvent {
  leg_id?: string | null;
  truck_id?: string | null;
  load_status?: string | null;
  created_at?: string | null;
}

export type LegLoadStatus = "empty" | "loaded" | null;

export interface LegVerification {
  verified: boolean;
  loadStatus: LegLoadStatus;
}

/**
 * Newest Verify-screen event for this leg. Photo-upload stubs (null
 * `truck_id`) and legacy rows without a leg are ignored.
 */
export function resolveLegVerification(
  events: readonly DriverVerifiedEvent[] | null | undefined,
  legId: string | null | undefined,
): LegVerification {
  if (!legId || !events?.length) return { verified: false, loadStatus: null };
  const mine = events
    .filter((e) => e.leg_id === legId && e.truck_id != null && e.truck_id !== "")
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
  const newest = mine[0];
  if (!newest) return { verified: false, loadStatus: null };
  const ls = newest.load_status;
  return { verified: true, loadStatus: ls === "empty" || ls === "loaded" ? ls : null };
}

/** BOL is waived only when Verify applies and this leg was verified Empty. Fails closed. */
export function isBolRequiredForDeparture(params: {
  verifyRequired: boolean;
  verification: LegVerification;
}): boolean {
  const { verifyRequired, verification } = params;
  return !(verifyRequired && verification.verified && verification.loadStatus === "empty");
}

export interface DepartureChecklistState {
  verifyRequired: boolean;
  verified: boolean;
  trailerPhotoLeftCount: number;
  trailerPhotoRightCount: number;
  licensePlatePhotoCount: number;
  bolCount: number;
  bolRequired: boolean;
}

/** Ordered missing-item labels for the departure message (today's copy). */
export function departureChecklistMissing(s: DepartureChecklistState): string[] {
  const missing: string[] = [];
  if (s.verifyRequired && !s.verified) missing.push("verify trailer info");
  if (s.trailerPhotoLeftCount === 0) missing.push("upload left-side trailer photo");
  if (s.trailerPhotoRightCount === 0) missing.push("upload right-side trailer photo");
  if (s.licensePlatePhotoCount === 0) missing.push("upload license plate photo");
  if (s.bolRequired && s.bolCount === 0) missing.push("upload BOL");
  return missing;
}
